import { autoinject, bindable, observable } from 'aurelia-framework';
import { FileAttachmentModels } from 'models/FileAttachmentModels';
import { SystemFileStorageService } from 'services/system-file-storage-service';
import { PdfPreviewController } from './pdf-preview-controller';
import { AsyncErrorHandler } from 'lib/ui';

export type SystemFileItem = {
  fileId: number;
  fileName?: string;
  isPdf?: boolean;
};

@autoinject
export class SystemFileList {
  @bindable()
  @observable()
  fileIds: number[] = [];

  @bindable()
  disabled = false;

  @bindable()
  fileOnly = false;

  @bindable()
  accept = '.pdf';

  @bindable()
  onFilesSelected: ({ files }: { files: File[] }) => void;

  @bindable()
  onFileRemoved: ({ fileId }: { fileId: number }) => void;

  protected files: SystemFileItem[] = [];

  constructor(
    private systemFileStorageService: SystemFileStorageService,
    private pdfPreviewController: PdfPreviewController
  ) {}

  fileIdsChanged(fileIds: number[]) {
    if (!fileIds?.length) {
      this.files = [];
      return;
    }
    void this.loadMetadata(fileIds);
  }

  private async loadMetadata(fileIds: number[]) {
    this.files = await Promise.all(
      fileIds.map(async (fileId) => {
        const meta = await this.systemFileStorageService.getMetaData(fileId);
        return {
          fileId,
          fileName: meta?.FileName,
          isPdf: meta?.FileName?.endsWith('.pdf') ?? false,
        };
      })
    );
  }

  @AsyncErrorHandler
  protected async previewFile(file: SystemFileItem) {
    const blob = (await this.systemFileStorageService.download(file.fileId, false)) as Blob;
    void this.pdfPreviewController.openPdfPreview({
      blob,
      fileName: file.fileName,
    });
  }

  @AsyncErrorHandler
  protected async downloadFile(file: SystemFileItem) {
    await this.systemFileStorageService.download(file.fileId, true);
  }

  protected removeFile(file: SystemFileItem) {
    this.onFileRemoved?.({ fileId: file.fileId });
  }

  protected addFiles(event: Event) {
    const target = event.target as HTMLInputElement;
    const files = Array.from(target.files ?? []);
    if (files.length) {
      this.onFilesSelected?.({ files });
    }
    target.value = '';
  }
}
